import { makeAutoObservable, runInAction } from 'mobx';
import { FileTypeEnum, ImportTypeEnum } from '@app/common';
import type { TradeStore } from './trade-store';
import type { RootStore } from './root-store';
import i18n from '../../translation/i18n';
import { dispatchModalEvent, waitForRender } from '../../utils/dom-observers';

export interface ImportResultWarning {
    message: string;
    link?: string;
}

export class TradeImportDialogStore {
    rootStore: RootStore;

    tradeStore: TradeStore;

    opened = false;

    file: File | null = null;

    importType: ImportTypeEnum = ImportTypeEnum.Degiro;

    loading = false;

    importResultMessage: string | null = null;

    importResultWarnings: ImportResultWarning[] = [];

    notFoundSymbols: string[] = [];

    notFoundSymbolsDialogOpened: boolean = false;

    pendingNotFoundSymbolsDialog: boolean = false;

    recalculateAfterImport: boolean = true;

    importedRowsCount: number = 0;

    openPromise?: Promise<void>;

    constructor(tradeStore: TradeStore) {
        this.rootStore = tradeStore.rootStore;
        this.tradeStore = tradeStore;

        makeAutoObservable(this, {
            rootStore: false,
            tradeStore: false,
            openPromise: false,
        });
    }

    setImportResultMessage(text: string) {
        this.importResultMessage = text;
    }

    setImportResultWarnings(warnings: ImportResultWarning[]) {
        this.importResultWarnings = warnings;
    }

    setNotFoundSymbols(symbols: string[]) {
        this.notFoundSymbols = symbols;
    }

    queueNotFoundSymbolsDialog(symbols: string[]) {
        this.notFoundSymbols = symbols;
        this.pendingNotFoundSymbolsDialog = symbols.length > 0;
    }

    openQueuedNotFoundSymbolsDialogIfAny() {
        if (this.pendingNotFoundSymbolsDialog && this.notFoundSymbols.length > 0) {
            this.notFoundSymbolsDialogOpened = true;
            this.pendingNotFoundSymbolsDialog = false;
        }
    }

    openNotFoundSymbolsDialog() {
        if (this.notFoundSymbols.length > 0) {
            this.notFoundSymbolsDialogOpened = true;
            this.pendingNotFoundSymbolsDialog = false;
        }
    }

    closeNotFoundSymbolsDialog() {
        this.notFoundSymbolsDialogOpened = false;
        this.notFoundSymbols = [];
        this.pendingNotFoundSymbolsDialog = false;
    }

    openImportDialog(): void {
        this.opened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    /**
     * Promise-based modal opening for onboarding
     * Resolves when modal is fully rendered and ready
     */
    async openImportDialogAsync(): Promise<void> {
        this.openPromise = new Promise<void>((resolve) => {
            // Set opened state
            runInAction(() => {
                this.opened = true;
            });

            this.rootStore.alertStore.setModalPlacement();

            // Wait for render cycle and then resolve
            waitForRender().then(() => {
                // Dispatch event for onboarding system
                dispatchModalEvent('import-modal-ready', {
                    modalType: 'import',
                    opened: this.opened,
                });
                resolve();
            });
        });

        return this.openPromise;
    }

    closeImportDialog(): void {
        this.opened = false;
        this.rootStore.alertStore.setDefaultPlacement();
        this.resetUploadedFile();
    }

    resetUploadedFile(): void {
        this.file = null;
    }

    setFile(file: File) {
        this.file = file;
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    setImportType(importType: ImportTypeEnum): void {
        this.importType = importType;
    }

    setRecalculateAfterImport(recalculateAfterImport: boolean): void {
        this.recalculateAfterImport = recalculateAfterImport;
    }

    async importFile() {
        this.importResultMessage = null;
        this.importResultWarnings = [];
        this.notFoundSymbols = [];
        this.notFoundSymbolsDialogOpened = false;
        this.pendingNotFoundSymbolsDialog = false;

        try {
            const formData = new FormData();
            if (!this.file) {
                throw new Error('No uploaded file');
            }
            formData.append('trade', this.file);
            this.startLoading();

            const fileType = this.file.name.split('.')[1].toUpperCase() as FileTypeEnum;
            const response = await this.rootStore.apiClient.tradeImportController.tradeImportControllerImport(
                {
                    portfolioId: this.rootStore.portfolioStore.selectedPortfolio.id,
                    importType: this.importType,
                    processTrades: this.recalculateAfterImport,
                    fileType,
                },
                { body: formData },
            );

            if (!response) {
                throw new Error('No response from trade import');
            }

            if (response.invalidFormat) {
                this.importResultWarnings.push({ message: i18n.t(`blocks.trades.invalidFormat`) });
                return;
            }

            if (response.importedRowsCount <= 0) {
                if (response.existingTradesCount > 0) {
                    this.importResultWarnings.push({ message: i18n.t(`blocks.trades.allRowsExisted`) });
                    return;
                }

                this.importResultWarnings.push({ message: i18n.t(`blocks.trades.noValidRows`) });
            }

            let importedResultMessage = i18n.t(`blocks.trades.imported.successfully`, {
                importedRowsCount: response.importedRowsCount,
            });
            if (response.existingTradesCount) {
                importedResultMessage += i18n.t(`blocks.trades.imported.existed`, {
                    existingTradesCount: response.existingTradesCount,
                });
            }
            // if (response.skippedRowsCount) {
            //     importedResultMessage += i18n.t(`blocks.trades.imported.skipped`, {
            //         skippedRowsCount: response.skippedRowsCount,
            //     });
            // }

            if (response.notFoundCurrencies.length) {
                this.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.notFoundCurrencies`, {
                        currencies: response.notFoundCurrencies.join(', '),
                    }),
                });
            }

            if (response.notFoundSymbols.length) {
                this.setNotFoundSymbols(response.notFoundSymbols);
            }

            if (response.invalidRowDates) {
                this.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.invalidRowDates`, {
                        invalidRowDates: response.invalidRowDates,
                    }),
                });
            }

            if (response.hasBlacklistedTransactions) {
                this.importResultWarnings.push({
                    message: i18n.t(`blocks.trades.blacklistedOperations`),
                });
            }

            runInAction(() => {
                this.importedRowsCount = response.importedRowsCount;
                this.importResultMessage = importedResultMessage;

                if (response.importedRowsCount !== 0) {
                    if (this.recalculateAfterImport) {
                        this.rootStore.cookieService.setImportResultMessage(importedResultMessage);

                        if (this.importResultWarnings.length > 0) {
                            this.rootStore.cookieService.setImportResultWarnings(this.importResultWarnings);
                        }

                        if (this.notFoundSymbols.length > 0) {
                            this.rootStore.cookieService.setImportNotFoundSymbols(this.notFoundSymbols);
                        }

                        this.rootStore.portfolioStore.selectedPortfolio.setIsRecalculating(true);
                    } else {
                        if (this.notFoundSymbols.length > 0) {
                            this.queueNotFoundSymbolsDialog(this.notFoundSymbols);
                        }

                        this.tradeStore.setSelectedLastImported(true);
                        this.tradeStore.lastImportedBroker = this.importType;
                        this.rootStore.portfolioStore.fetchPortfolioList();
                        this.tradeStore.fetchTradeList();
                    }
                } else if (this.notFoundSymbols.length > 0) {
                    this.queueNotFoundSymbolsDialog(this.notFoundSymbols);
                }
            });
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.stopLoading();
        }
    }

    clearImportResultMessage() {
        this.importResultMessage = null;
    }

    clearImportResultWarnings() {
        this.importResultWarnings = [];
    }

    resetMessages() {
        this.importResultMessage = null;
        this.importResultWarnings = [];
    }
}
